import {
  NotificationOptionDTO,
  SupplierBaseDTO,
  SupplierConfigurationFeatureDTO,
  SupplierDTO,
  SupplierMinimalDTO,
} from '../dto/SupplierDTO';

export type NotificationOption = 'ignore' | 'notify';
export type SupplierConfigurationFeature = 'agreementPrice' | 'priceSuggestion';

export const notificationOptionFromDtoMap: Record<NotificationOptionDTO, NotificationOption> = {
  IGNORE: 'ignore',
  NOTIFY: 'notify',
};

export const notificationOptionToDtoMap: Record<NotificationOption, NotificationOptionDTO> = {
  ignore: 'IGNORE',
  notify: 'NOTIFY',
};

export const supplierConfigurationFeatureFromDtoMap: Record<
  SupplierConfigurationFeatureDTO,
  SupplierConfigurationFeature
> = {
  AGREEMENT_PRICE: 'agreementPrice',
  PRICE_SUGGESTION: 'priceSuggestion',
};

export const supplierConfigurationFeatureToDtoMap: Record<
  SupplierConfigurationFeature,
  SupplierConfigurationFeatureDTO
> = {
  agreementPrice: 'AGREEMENT_PRICE',
  priceSuggestion: 'PRICE_SUGGESTION',
};

export interface SupplierConfiguration {
  priceChangeNotificationConfig?: NotificationOption;
}

export interface SupplierProps {
  mgSupplierId: number;
  finfoSupplierId: number;
  name: string;
  gln: number;
  purchaseAgreementCount: number;
  hasSelectedSupplierAgreement: boolean;
  nonStockItemSupplier: boolean;
  config?: SupplierConfiguration;
  purchaser?: string;
  vilmaLevel?: string;
  orgNumber?: string;
  address?: string;
  postalCode?: string;
  email?: string;
  phoneNumber?: string;
  city?: string;
  type?: string;
}

export class Supplier {
  mgSupplierId: number;
  finfoSupplierId: number;
  name: string;
  gln: number;
  purchaseAgreementCount: number;
  hasSelectedSupplierAgreement: boolean;
  nonStockItemSupplier: boolean;
  config?: SupplierConfiguration;
  purchaser?: string;
  vilmaLevel?: string;
  orgNumber?: string;
  address?: string;
  postalCode?: string;
  email?: string;
  phoneNumber?: string;
  city?: string;
  type?: string;

  constructor({
    mgSupplierId,
    name,
    gln,
    finfoSupplierId,
    purchaseAgreementCount,
    hasSelectedSupplierAgreement,
    nonStockItemSupplier,
    config,
    purchaser,
    vilmaLevel,
    orgNumber,
    address,
    postalCode,
    email,
    phoneNumber,
    city,
    type,
  }: SupplierProps) {
    this.mgSupplierId = mgSupplierId;
    this.finfoSupplierId = finfoSupplierId;
    this.name = name;
    this.gln = gln;
    this.purchaseAgreementCount = purchaseAgreementCount;
    this.hasSelectedSupplierAgreement = hasSelectedSupplierAgreement;
    this.nonStockItemSupplier = nonStockItemSupplier;
    this.config = config;
    this.purchaser = purchaser;
    this.vilmaLevel = vilmaLevel;
    this.orgNumber = orgNumber;
    this.address = address;
    this.postalCode = postalCode;
    this.email = email;
    this.phoneNumber = phoneNumber;
    this.city = city;
    this.type = type;
  }

  static fromDTO = (dto: SupplierDTO): Supplier => {
    const priceSuggestionConfiguration = dto.supplierConfiguration?.notificationConfig?.find(
      (config) => config.type === 'PRICE_SUGGESTION'
    );
    return new Supplier({
      mgSupplierId: dto.mgSupplierId,
      name: dto.participantAccountName,
      gln: dto.gln,
      finfoSupplierId: dto.finfoSupplierId,
      purchaseAgreementCount: dto.purchaseAgreementCount || 0,
      hasSelectedSupplierAgreement: dto.hasSelectedSupplierAgreement,
      nonStockItemSupplier: dto.nonStockItemSupplier,
      config: priceSuggestionConfiguration
        ? {
            priceChangeNotificationConfig:
              notificationOptionFromDtoMap[priceSuggestionConfiguration.value],
          }
        : undefined,
      purchaser: dto.purchaser,
      vilmaLevel: dto.vilmaLevel,
      orgNumber: dto.orgNumber,
      address: dto.address,
      postalCode: dto.postalCode,
      email: dto.email,
      phoneNumber: dto.phoneNumber,
      city: dto.city,
      type: dto.type,
    });
  };
}

export interface SupplierMinimalProps {
  mgSupplierId: number;
  name: string;
}
export class SupplierMinimal {
  mgSupplierId: number;
  name: string;

  constructor({ mgSupplierId, name }: SupplierMinimalProps) {
    this.mgSupplierId = mgSupplierId;
    this.name = name;
  }

  static fromSupplierDTO = (dto: SupplierDTO): SupplierMinimal => {
    return new SupplierMinimal({
      mgSupplierId: dto.mgSupplierId,
      name: dto.participantAccountName,
    });
  };

  static fromDTO = (dto: SupplierMinimalDTO): SupplierMinimal => {
    return new SupplierMinimal({
      mgSupplierId: dto.mgSupplierId,
      name: dto.name,
    });
  };
}

export interface SupplierBaseProps {
  id: number;
  supplierNumber: number;
  name: string;
}

export class SupplierBase {
  id: number;
  supplierNumber: number;
  name: string;

  constructor({ id, supplierNumber, name }: SupplierBaseProps) {
    this.id = id;
    this.supplierNumber = supplierNumber;
    this.name = name;
  }

  static fromDto = (dto: SupplierBaseDTO): SupplierBase =>
    new SupplierBase({
      id: dto.id,
      supplierNumber: dto.supplierNumber,
      name: dto.name,
    });

  static fromSupplierDTO = (dto: SupplierDTO): SupplierBase =>
    new SupplierBase({
      id: dto.id,
      supplierNumber: dto.mgSupplierId,
      name: dto.participantAccountName,
    });
}
